import React, { useCallback } from "react";
import { Chart } from "react-google-charts";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { Box, CircularProgress } from "@mui/material";
import { getBusinessStats } from "../../../services/MasterApi";

export const Dashboard = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const businessStats = useSelector((state) => state.users.businessStats);
  let { value: busiTransData, isStarted, isError, error } = businessStats;

  const [businessType] = React.useState([
    { id: 0, name: "All" },
    { id: 1, name: "LLC" },
    { id: 2, name: "Corpo" },
    { id: 3, name: "DBA" },
  ]);

  let jwt_token = sessionStorage.getItem("bb_token");

  const fetchStats = useCallback(() => {
    if (jwt_token) {
      dispatch(getBusinessStats(businessType[0].id, new Date(), new Date(), jwt_token));
    } else {
      navigate("/login");
    }
  }, [dispatch, jwt_token, businessType, navigate]);

  React.useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  const llcCount =
    busiTransData?.charts?.completedBusiness?.LLC || 0;
  const corpCount =
    busiTransData?.charts?.completedBusiness?.CORP || 0;
  const dbaCount =
    busiTransData?.charts?.completedBusiness?.DBA || 0;

  const hasData = llcCount > 0 || corpCount > 0 || dbaCount > 0;

  const data = hasData
    ? [
        ["Business Type", "Count", { role: "style" }],
        ["LLC", llcCount, "#1a73e8"],
        ["Corporation", corpCount, "#137333"],
        ["DBA", dbaCount, "#e37400"],
      ]
    : [
        ["Business Type", "Count"],
        ["No Completed Data", 0],
      ];

  const options = {
    title: "",
    legend: { position: "none" },
    chartArea: { width: "85%", height: "75%" },
    hAxis: {
      title: "Completed Registrations",
      minValue: 0,
      textStyle: { color: "#5f6368", fontSize: 12 },
      titleTextStyle: { color: "#202124", bold: true, fontSize: 13 },
      gridlines: { color: "#f1f3f4" },
    },
    vAxis: {
      textStyle: { color: "#202124", fontSize: 13, bold: true },
    },
    backgroundColor: "transparent",
    tooltip: hasData ? { trigger: "show" } : { trigger: "none" },
  };

  const statCards = [
    {
      name: "New Applications",
      count: busiTransData?.transactionCounts?.newBusiness || 0,
      icon: "fa-solid fa-file-circle-plus",
      color: "#1a73e8",
      bg: "#e8f0fe",
      trend: "Action Required",
      path: "/table",
    },
    {
      name: "In Progress",
      count: busiTransData?.transactionCounts?.inProgressBusiness || 0,
      icon: "fa-solid fa-clock-rotate-left",
      color: "#e37400",
      bg: "#fef7e0",
      trend: "Under State Review",
      path: "/table",
    },
    {
      name: "Completed Formations",
      count: busiTransData?.transactionCounts?.completedBusiness || 0,
      icon: "fa-solid fa-circle-check",
      color: "#137333",
      bg: "#e6f4ea",
      trend: "Approved & Active",
      path: "/table",
    },
  ];

  return (
    <div className="min-h-[calc(100vh-72px)] bg-[#f8fafc] py-6 px-4 sm:px-8 font-sans">
      <div className="w-full space-y-6">
        {/* Top Header & Actions Bar */}
        <div className="bg-white rounded-xl border border-slate-200/90 p-4 sm:p-5 shadow-2xs flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-slate-900 tracking-tight">
              Operational Overview
            </h1>
            <p className="text-xs text-slate-500 mt-0.5">
              Real-time business formation filings and state compliance analytics.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2.5">
            <button
              type="button"
              onClick={() => navigate("/table")}
              className="px-3 py-1.5 bg-[#1a73e8] hover:bg-[#1557b0] text-white text-xs font-semibold rounded-lg shadow-2xs transition-colors cursor-pointer inline-flex items-center gap-1.5"
            >
              <i className="fa-solid fa-table-list"></i>
              <span>Registered Businesses</span>
            </button>

            <button
              type="button"
              onClick={() => navigate("/transactions_list")}
              className="px-3 py-1.5 bg-white hover:bg-slate-50 border border-slate-200 text-slate-700 text-xs font-semibold rounded-lg shadow-2xs transition-colors cursor-pointer inline-flex items-center gap-1.5"
            >
              <i className="fa-solid fa-credit-card text-[#1a73e8]"></i>
              <span>Payment Ledger</span>
            </button>

            <button
              type="button"
              onClick={fetchStats}
              disabled={isStarted}
              className="p-1.5 bg-white hover:bg-slate-50 border border-slate-200 text-slate-600 rounded-lg shadow-2xs transition-colors"
              title="Refresh statistics"
            >
              <i className={`fa-solid fa-arrow-rotate-right text-xs ${isStarted ? "animate-spin text-[#1a73e8]" : ""}`}></i>
            </button>
          </div>
        </div>

        {isStarted ? (
          <Box
            sx={{
              width: "100%",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              flexDirection: "column",
              height: "40vh",
            }}
          >
            <CircularProgress size={36} sx={{ color: "#1a73e8" }} />
            <p style={{ color: "#5f6368", marginTop: "1rem", fontSize: "14px" }}>
              Loading real-time metrics...
            </p>
          </Box>
        ) : isError ? (
          <div className="bg-red-50 border border-red-200 rounded-2xl p-8 text-center text-red-700">
            <i className="fa-solid fa-circle-exclamation text-2xl mb-2"></i>
            <h3 className="font-semibold text-base">Failed to load statistics: {error}</h3>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {statCards.map((ele, index) => (
              <div
                key={index}
                onClick={() => navigate(ele.path)}
                className="bg-white rounded-xl border border-slate-200/90 p-5 shadow-2xs hover:border-slate-300 hover:shadow-xs transition-all duration-200 cursor-pointer"
              >
                <div className="flex items-center justify-between mb-3">
                  <div
                    className="w-10 h-10 rounded-lg flex items-center justify-center text-base"
                    style={{ backgroundColor: ele.bg, color: ele.color }}
                  >
                    <i className={ele.icon}></i>
                  </div>
                  <span className="text-[11px] font-semibold px-2.5 py-0.5 rounded-full bg-slate-100 text-slate-600">
                    {ele.trend}
                  </span>
                </div>
                <h2 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">
                  {ele.name}
                </h2>
                <div className="flex items-baseline justify-between">
                  <h3 className="text-3xl font-bold text-slate-900 tracking-tight">{ele.count}</h3>
                  <span className="text-xs text-[#1a73e8] font-semibold hover:underline inline-flex items-center gap-1">
                    View list <i className="fa-solid fa-arrow-right text-[10px]"></i>
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Chart Section */}
        <div className="bg-white rounded-xl border border-slate-200/90 p-5 sm:p-6 shadow-2xs">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between pb-3.5 mb-4 border-b border-slate-100 gap-2">
            <div>
              <h2 className="text-sm font-bold text-slate-900 uppercase tracking-wider leading-tight">
                Completed Entity Distribution
              </h2>
              <p className="text-xs text-slate-500 mt-0.5">
                Breakdown of approved state company registrations by legal structure
              </p>
            </div>
            <span className="self-start sm:self-auto text-xs font-semibold text-[#1a73e8] bg-blue-50 border border-blue-100 px-3 py-1 rounded-full">
              Live Verified Data
            </span>
          </div>

          <Chart
            chartType="BarChart"
            data={data}
            options={options}
            width={"100%"}
            height={"320px"}
          />
        </div>
      </div>
    </div>
  );
};

export default Dashboard;